import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDate, formatDateTime } from '@/lib/format'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusBadge } from '@/components/StatusBadge'
import { Pagination } from '@/components/Pagination'
import { EmptyState } from '@/components/EmptyState'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { ContactPreviewPanel } from '@/components/ContactPreviewPanel'
import { ListChecks, StopCircle, Loader2, Pencil } from 'lucide-react'
import { useEnrollments, useSequences, useStopEnrollment } from '@/hooks/useApi'
import type { Enrollment, EnrollmentStatus } from '@/types/api'
import { ViewModeSwitch } from '@/components/ViewModeSwitch'
import { useViewModeParam } from '@/hooks/useViewMode'

const STATUS_VALUES: (EnrollmentStatus | 'all')[] = [
  'all',
  'active',
  'paused',
  'completed',
  'replied',
  'bounced',
  'unsubscribed',
  'contact_deleted',
  'do_not_contact',
]

export function Enrollments() {
  const { t } = useTranslation(['enrollments', 'common'])
  const viewMode = useViewModeParam()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [sequenceFilter, setSequenceFilter] = useState<string>('all')
  const [previewEnrollment, setPreviewEnrollment] = useState<Enrollment | null>(null)

  const { data: sequences } = useSequences()
  const { data: enrollments, isLoading } = useEnrollments(page, 50, {
    status: statusFilter !== 'all' ? statusFilter : undefined,
    sequence_id: sequenceFilter !== 'all' ? sequenceFilter : undefined,
    viewMode,
  })
  const stopEnrollment = useStopEnrollment()

  const handleStop = (id: string) => {
    if (confirm(t('stopConfirm'))) {
      stopEnrollment.mutate(id)
    }
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">{t('title')}</h1>
        <ViewModeSwitch />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('card.title')}</CardTitle>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder={t('filters.statusPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_VALUES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {value === 'all'
                        ? t('filters.allStatuses')
                        : t(`common:status.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sequenceFilter} onValueChange={setSequenceFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={t('filters.sequencePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('filters.allSequences')}</SelectItem>
                  {sequences?.map((sequence) => (
                    <SelectItem key={sequence.id} value={sequence.id}>
                      {sequence.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : enrollments && enrollments.items.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('table.contact')}</TableHead>
                    <TableHead>{t('table.sequence')}</TableHead>
                    <TableHead>{t('table.status')}</TableHead>
                    <TableHead className="text-center">{t('table.step')}</TableHead>
                    <TableHead>{t('table.nextStepAt')}</TableHead>
                    <TableHead>{t('table.started')}</TableHead>
                    <TableHead className="text-right">{t('table.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrollments.items.map((enrollment) => (
                    <TableRow key={enrollment.id}>
                      <TableCell>
                        {enrollment.contact ? (
                          <div>
                            <p className="font-medium">{enrollment.contact.full_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {enrollment.contact.email}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">{t('unknownContact')}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {enrollment.sequence ? (
                          <Link
                            to={`/sequences/${enrollment.sequence.id}`}
                            className="hover:underline"
                          >
                            {enrollment.sequence.name}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">{t('unknownSequence')}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={enrollment.status} />
                      </TableCell>
                      <TableCell className="text-center">
                        {enrollment.current_step}
                      </TableCell>
                      <TableCell>
                        {enrollment.next_step_at
                          ? formatDateTime(enrollment.next_step_at)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {formatDate(enrollment.started_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPreviewEnrollment(enrollment)}
                          aria-label={t('common:contactPreview.openPreview')}
                          title={t('common:contactPreview.openPreview')}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {enrollment.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleStop(enrollment.id)}
                            disabled={stopEnrollment.isPending}
                          >
                            {stopEnrollment.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <StopCircle className="h-4 w-4 text-red-500" />
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                page={page}
                totalPages={enrollments.pages}
                onPageChange={setPage}
              />
            </>
          ) : (
            <EmptyState
              icon={ListChecks}
              title={t('empty.title')}
              description={
                statusFilter !== 'all' || sequenceFilter !== 'all'
                  ? t('empty.descriptionFiltered')
                  : t('empty.description')
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Anteprima e personalizzazione step per contatto (#220) */}
      <Dialog
        open={!!previewEnrollment}
        onOpenChange={(open) => { if (!open) setPreviewEnrollment(null) }}
      >
        <DialogContent className="sm:max-w-2xl">
          {previewEnrollment && (
            <ContactPreviewPanel
              sequenceId={previewEnrollment.sequence_id}
              contact={{
                id: previewEnrollment.contact_id,
                name: previewEnrollment.contact?.full_name || previewEnrollment.contact?.email || '',
                email: previewEnrollment.contact?.email || '',
              }}
              enrollmentId={previewEnrollment.id}
              onBack={() => setPreviewEnrollment(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
